import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { apiRequest, thinkApiRequest } from "../Auth/AuthConfig.tsx";
import {
  IdTokenClaims,
  InteractionStatus,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

interface CustomIdTokenClaims extends IdTokenClaims {
  oid?: string;
}

const useAccessToken = () => {
  const { accounts, instance, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [error, setError] = useState<Error | null>(null);
  const [thinkAccessToken, setThinkAccessToken] = useState<string | null>(null);
  const redirectInFlightRef = useRef(false);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      redirectInFlightRef.current = false;
    }
  }, [inProgress]);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (accounts.length > 0 && instance) {
        if (inProgress !== InteractionStatus.None) {
          return;
        }

        const request = {
          ...apiRequest,
          account: instance.getActiveAccount() ?? accounts[0],
        };

        try {
          const response = await instance.acquireTokenSilent(request);
          localStorage.setItem("lbmc_token", response.accessToken);
          setAccessToken(response.accessToken);

          const claims = response.idTokenClaims as CustomIdTokenClaims;
          setUserId(claims.oid || "");

          if (response.expiresOn) {
            const timeToExpire = response.expiresOn.getTime() - Date.now();
            setTimeout(
              fetchAccessToken,
              Math.max(timeToExpire - 120000, 10000)
            );
          }
        } catch (err: any) {
          if (err instanceof InteractionRequiredAuthError) {
            if (redirectInFlightRef.current || inProgress !== InteractionStatus.None) {
              return;
            }
            redirectInFlightRef.current = true;
            instance.acquireTokenRedirect(request);
            // const interactiveResponse = await instance.acquireTokenPopup(request);
            // setAccessToken(interactiveResponse.accessToken);
            // localStorage.setItem("lbmc_token", interactiveResponse.accessToken);
          } else {
            console.error("Error fetching access token:", err);
            setError(err);
          }
        }
      }
    };

    const fetchThinkAccessToken = async () => {
      if (accounts.length > 0 && instance) {
        if (inProgress !== InteractionStatus.None) {
          return;
        }

        const request = {
          ...thinkApiRequest,
          account: instance.getActiveAccount() ?? accounts[0],
        };

        try {
          const response = await instance.acquireTokenSilent(request);
          setThinkAccessToken(response.accessToken);

          if (response.expiresOn) {
            const timeToExpire = response.expiresOn.getTime() - Date.now();
            setTimeout(
              fetchThinkAccessToken,
              Math.max(timeToExpire - 120000, 10000)
            );
          }
        } catch (err: any) {
          if (err instanceof InteractionRequiredAuthError) {
            if (redirectInFlightRef.current || inProgress !== InteractionStatus.None) {
              return;
            }
            redirectInFlightRef.current = true;
            instance.acquireTokenRedirect(request);
            // Uncomment the following lines if you want to handle interactive token acquisition
            // const interactiveResponse = await instance.acquireTokenPopup(request);
            // setAccessToken(interactiveResponse.accessToken);
            // localStorage.setItem("lbmc_token", interactiveResponse.accessToken);
          } else {
            console.error("Error fetching access token:", err);
            setError(err);
          }
        }
      }
    };

    fetchAccessToken();
    fetchThinkAccessToken();
  }, [accounts, instance, inProgress]);

  return { accessToken, userId, error, thinkAccessToken };
};

export default useAccessToken;
